import React, { useState, useEffect, useCallback } from 'react';
import { Form, Input, Select, Button, Space, DatePicker, Table, InputNumber, message, Alert, Checkbox, Popconfirm, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { SupplierQuote, Supplier, QuoteStatus, Product, CustomerOrder } from '../types.ts';
import { productAPI, customerOrderAPI } from '../services/api.ts';
import { formatCurrency, inputNumberFormatter, inputNumberParser } from '../utils/numberFormatting.ts';
import { formatProductDisplay } from '../utils/productUtils.ts';
import { QUOTE_CREATABLE_STATUS_IDS, QUOTE_DEFAULT_CREATE_STATUS_ID, getQuoteStatusColor } from '../utils/quoteStatus.ts';

const { Option } = Select;
const { TextArea } = Input;

// Form preventivo fornitore, gemello di SupplierOrderForm: numero in maiuscolo,
// ordine cliente collegato (solo ordini aperti), righe con costo unitario e giorni di
// consegna, stato solo in creazione, modalità "solo testata" dopo l'accettazione.
interface SupplierQuoteFormProps {
  initialValues?: SupplierQuote | null;
  suppliers: Supplier[];
  quoteStatuses: QuoteStatus[];
  onSubmit: (values: any) => void;
  onCancel: () => void;
  /** Preventivo accettato: righe in sola lettura, si salva solo la testata */
  headerOnly?: boolean;
}

interface QuoteRow {
  key: number;
  product_id?: number;
  quantity: number;
  unit_cost: number;
  total_cost: number;
  delivery_time_days?: number | null;
  notes: string;
}

const SupplierQuoteForm: React.FC<SupplierQuoteFormProps> = ({
  initialValues,
  suppliers,
  quoteStatuses,
  onSubmit,
  onCancel,
  headerOnly = false,
}) => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState<Product[]>([]);
  const [customerOrders, setCustomerOrders] = useState<CustomerOrder[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [rows, setRows] = useState<QuoteRow[]>([]);
  const [filterBySupplier, setFilterBySupplier] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);

  const loadProducts = useCallback(async (supplierId?: number) => {
    try {
      const data = await productAPI.getProducts(undefined, undefined, supplierId);
      setProducts(data);
    } catch (error) {
      message.error('Errore nel caricamento dei prodotti');
    }
  }, []);

  // Ordini cliente collegabili: solo quelli aperti (stati 1-5), come nel form ordine fornitore
  const loadCustomerOrders = useCallback(async (linkedId?: number | null) => {
    setLoadingOrders(true);
    try {
      const data = await customerOrderAPI.getCustomerOrders({ exclude_completed: true, limit: 1000 });
      const activeOrders = data.filter((order) => [1, 2, 3, 4, 5].includes(order.order_status_id));
      if (linkedId && !activeOrders.some((order) => order.id === linkedId)) {
        const linkedOrder = data.find((order) => order.id === linkedId)
          || await customerOrderAPI.getCustomerOrder(linkedId).catch(() => null);
        if (linkedOrder) activeOrders.push(linkedOrder);
      }
      setCustomerOrders(activeOrders);
    } catch (error) {
      message.error('Errore nel caricamento degli ordini cliente');
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  useEffect(() => {
    loadCustomerOrders(initialValues?.customer_order_id);
  }, [initialValues?.customer_order_id, loadCustomerOrders]);

  useEffect(() => {
    loadProducts(filterBySupplier && selectedSupplierId ? selectedSupplierId : undefined);
  }, [filterBySupplier, selectedSupplierId, loadProducts]);

  useEffect(() => {
    if (initialValues) {
      setSelectedSupplierId(initialValues.supplier_id);
      setRows((initialValues.quote_details || []).map((d, index) => ({
        key: d.id ?? index,
        product_id: d.product_id,
        quantity: d.quantity,
        unit_cost: Number(d.unit_cost ?? 0),
        total_cost: Number(d.total_cost ?? d.quantity * Number(d.unit_cost ?? 0)),
        delivery_time_days: d.delivery_time_days ?? null,
        notes: d.notes || '',
      })));
      form.setFieldsValue({
        supplier_id: initialValues.supplier_id,
        quote_number: initialValues.quote_number,
        quote_status_id: initialValues.quote_status_id,
        quote_date: initialValues.quote_date ? dayjs(initialValues.quote_date) : null,
        expiry_date: initialValues.expiry_date ? dayjs(initialValues.expiry_date) : null,
        customer_order_id: initialValues.customer_order_id || undefined,
        delivery_time_days: initialValues.delivery_time_days ?? undefined,
        notes: initialValues.notes || undefined,
      });
    } else {
      form.setFieldsValue({
        quote_date: dayjs(),
        expiry_date: dayjs().add(30, 'day'),
        quote_status_id: QUOTE_DEFAULT_CREATE_STATUS_ID,
      });
    }
  }, [initialValues, form]);

  const addRow = () => {
    setRows([...rows, { key: Date.now(), product_id: undefined, quantity: 1, unit_cost: 0, total_cost: 0, delivery_time_days: null, notes: '' }]);
  };

  const removeRow = (index: number) => setRows(rows.filter((_, i) => i !== index));

  const updateRow = (index: number, field: keyof QuoteRow, value: any) => {
    const updated: QuoteRow = { ...rows[index], [field]: value };
    if (field === 'product_id') {
      const product = products.find((p) => p.id === value);
      if (product && product.cost != null) {
        updated.unit_cost = Number(product.cost);
      }
    }
    updated.total_cost = (updated.quantity || 0) * (updated.unit_cost || 0);
    const next = [...rows];
    next[index] = updated;
    setRows(next);
  };

  const handleSubmit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    if (!headerOnly) {
      if (rows.length === 0) {
        message.error('Aggiungi almeno un prodotto al preventivo');
        return;
      }
      const invalidRow = rows.findIndex((r) => !r.product_id || !(r.quantity >= 1));
      if (invalidRow >= 0) {
        message.error(`Riga ${invalidRow + 1}: selezionare un prodotto e indicare una quantità di almeno 1`);
        return;
      }
    }
    const payload: any = {
      supplier_id: values.supplier_id,
      quote_number: values.quote_number,
      quote_date: values.quote_date ? values.quote_date.format('YYYY-MM-DD') : null,
      expiry_date: values.expiry_date ? values.expiry_date.format('YYYY-MM-DD') : null,
      customer_order_id: values.customer_order_id || null,
      delivery_time_days: values.delivery_time_days ?? null,
      notes: values.notes || null,
    };
    if (!initialValues) {
      payload.quote_status_id = values.quote_status_id || QUOTE_DEFAULT_CREATE_STATUS_ID;
    }
    if (!headerOnly) {
      payload.quote_details = rows.map((r) => ({
        product_id: r.product_id,
        quantity: r.quantity,
        unit_cost: r.unit_cost,
        delivery_time_days: r.delivery_time_days ?? null,
        notes: r.notes || null,
      }));
    }
    onSubmit(payload);
  };

  const creatableStatuses = quoteStatuses.filter((s) => QUOTE_CREATABLE_STATUS_IDS.includes(s.id));
  const totalAmount = rows.reduce((sum, r) => sum + (r.total_cost || 0), 0);

  const columns = [
    {
      title: 'Prodotto',
      dataIndex: 'product_id',
      width: '36%',
      ellipsis: true,
      render: (_: any, record: QuoteRow, index: number) => (
        <Select
          style={{ width: '100%' }}
          placeholder="Seleziona prodotto"
          value={record.product_id}
          onChange={(value) => updateRow(index, 'product_id', value)}
          disabled={headerOnly}
          showSearch
          optionFilterProp="children"
        >
          {products.map((product) => (
            <Option key={product.id} value={product.id} title={formatProductDisplay(product)}>
              {formatProductDisplay(product)}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Quantità',
      dataIndex: 'quantity',
      width: '9%',
      render: (_: any, record: QuoteRow, index: number) => (
        <InputNumber min={1} value={record.quantity} onChange={(value) => updateRow(index, 'quantity', value || 1)} disabled={headerOnly} style={{ width: '100%' }} size="small" />
      ),
    },
    {
      title: 'Costo Unit.',
      dataIndex: 'unit_cost',
      width: '13%',
      render: (_: any, record: QuoteRow, index: number) => (
        <InputNumber
          min={0}
          precision={4}
          step={0.0001}
          value={record.unit_cost}
          onChange={(value) => updateRow(index, 'unit_cost', value || 0)}
          disabled={headerOnly}
          formatter={inputNumberFormatter}
          parser={inputNumberParser}
          placeholder="0,0000"
          style={{ width: '100%' }}
          size="small"
        />
      ),
    },
    { title: 'Totale', dataIndex: 'total_cost', width: '11%', render: (total: number) => `€ ${formatCurrency(total || 0)}` },
    {
      title: 'Consegna gg',
      dataIndex: 'delivery_time_days',
      width: '9%',
      render: (_: any, record: QuoteRow, index: number) => (
        <InputNumber min={0} value={record.delivery_time_days ?? undefined} onChange={(value) => updateRow(index, 'delivery_time_days', value ?? null)} disabled={headerOnly} style={{ width: '100%' }} size="small" placeholder="gg" />
      ),
    },
    {
      title: 'Note',
      dataIndex: 'notes',
      width: '16%',
      render: (_: any, record: QuoteRow, index: number) => (
        <Input value={record.notes} onChange={(e) => updateRow(index, 'notes', e.target.value)} placeholder="Note opzionali" size="small" disabled={headerOnly} />
      ),
    },
    {
      title: 'Azioni',
      width: '6%',
      render: (_: any, record: QuoteRow, index: number) => (
        !headerOnly && (
          <Popconfirm title="Rimuovere questa riga?" onConfirm={() => removeRow(index)} okText="Sì" cancelText="No">
            <Button danger size="small" type="text" icon={<DeleteOutlined />} />
          </Popconfirm>
        )
      ),
    },
  ];

  return (
    <Form form={form} layout="vertical">
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {headerOnly && (
          <Alert
            type="info"
            showIcon
            message="Preventivo accettato"
            description="Si può modificare solo la testata: le righe dei prodotti sono in sola lettura."
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          <Form.Item name="quote_number" label="Numero Preventivo" rules={[{ required: true, message: 'Inserire il numero preventivo' }, { max: 50, message: 'Massimo 50 caratteri' }]}>
            <Input
              placeholder="Numero preventivo"
              style={{ textTransform: 'uppercase' }}
              onChange={(e) => form.setFieldValue('quote_number', e.target.value.toUpperCase())}
            />
          </Form.Item>

          <Form.Item name="supplier_id" label="Fornitore" rules={[{ required: true, message: 'Selezionare un fornitore' }]}>
            <Select placeholder="Seleziona fornitore" onChange={(value) => setSelectedSupplierId(value)} showSearch optionFilterProp="children" disabled={headerOnly}>
              {suppliers.map((supplier) => (
                <Option key={supplier.id} value={supplier.id}>{supplier.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="customer_order_id"
            label="Ordine Cliente Collegato (opzionale)"
            extra="Alla conversione l'ordine fornitore nasce collegato a questo ordine cliente"
          >
            <Select
              placeholder="Seleziona ordine cliente"
              allowClear
              showSearch
              optionFilterProp="children"
              loading={loadingOrders}
              notFoundContent={loadingOrders ? 'Caricamento...' : 'Nessun ordine disponibile'}
            >
              {customerOrders.map((order) => (
                <Option key={order.id} value={order.id}>
                  {order.order_number} - {order.customer?.company}
                  {order.order_date && ` (${new Date(order.order_date).toLocaleDateString('it-IT')})`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="quote_date" label="Data Preventivo" rules={[{ required: true, message: 'Inserire la data preventivo' }]}>
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="expiry_date" label="Data Scadenza">
            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="delivery_time_days" label="Giorni di Consegna" extra="Alla conversione propone la data di consegna dell'ordine fornitore">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="es. 30" />
          </Form.Item>

          <Form.Item
            name="quote_status_id"
            label="Stato Preventivo"
            rules={[{ required: true, message: 'Selezionare uno stato' }]}
            tooltip={initialValues
              ? "Lo stato non si modifica da qui: usare l'azione 'Cambia Stato'"
              : 'In creazione un preventivo può nascere in Bozza, Nuovo o Inviato'}
          >
            <Select disabled={!!initialValues}>
              {(initialValues ? quoteStatuses : creatableStatuses).map((status) => (
                <Option key={status.id} value={status.id}>
                  <Tag color={getQuoteStatusColor(status)}>{status.display_name}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Note" style={{ gridColumn: 'span 2' }}>
            <TextArea rows={2} placeholder="Note opzionali" />
          </Form.Item>
        </div>

        <div>
          <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Dettagli Prodotti</h3>
            <Space>
              <Checkbox checked={filterBySupplier} onChange={(e) => setFilterBySupplier(e.target.checked)} disabled={!selectedSupplierId}>
                <strong>Visualizza solo i prodotti associati al fornitore</strong>
              </Checkbox>
              {!headerOnly && (
                <Button type="dashed" icon={<PlusOutlined />} onClick={addRow}>Aggiungi Prodotto</Button>
              )}
            </Space>
          </div>

          <Table
            columns={columns}
            dataSource={rows}
            rowKey="key"
            pagination={false}
            size="small"
            bordered
            tableLayout="fixed"
            locale={{ emptyText: 'Nessun prodotto aggiunto' }}
            scroll={{ x: 'max-content' }}
          />

          <div style={{ marginTop: 12, textAlign: 'right' }}>
            <strong>Totale Preventivo: € {formatCurrency(totalAmount)}</strong>
          </div>
        </div>

        <div style={{ textAlign: 'right' }}>
          <Space>
            <Button onClick={onCancel}>Annulla</Button>
            <Button type="primary" onClick={handleSubmit}>
              {initialValues ? 'Aggiorna' : 'Crea'} Preventivo
            </Button>
          </Space>
        </div>
      </Space>
    </Form>
  );
};

export default SupplierQuoteForm;
